import React, { useState, useEffect, useContext } from "react";
import { getCompanySettings, updateCompanySettings, getGeolocationLimitList, createGeolocationLimit, deleteGeolocationLimit } from '../services/api_settings';
import { AuthContext } from '../contexts/auth';
import Swal from "sweetalert2";
import GeolocationModal from "./geolocation_modal";
import LocationPicker from "./location_picker_modal";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";
import Fetching from "./fetching";
import Empty from "./empty";
import CustomTooltipComponent from "./custom_tooltip_component";


const TimbraturaSetting = () => {
  const { token } = useContext(AuthContext);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [ingressoAnticipato, setIngressoAnticipato] = useState(0);
  const [uscitaAnticipata, setUscitaAnticipata] = useState(0);
  const [ingressoPosticipato, setIngressoPosticipato] = useState(0);
  const [uscitaPosticipata, setUscitaPosticipata] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showFlessibilitaOraria, setShowFlessibilitaOraria] = useState(false);
  const [modalLocation, setModalLocation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [showLimiteTerritorio, setShowLimiteTerritorio] = useState(false);
  const [isModalLocationOpen, setIsModalLocationOpen] = useState(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [isGeoLimitActive, setIsGeoLimitActive] = useState(false);
  const [radius, setRadius] = useState(0);


  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getCompanySettings(token, 'attendance');

        setOriginalSettings(settings);
        setIngressoAnticipato(settings?.checkin_threshold_early || 0);
        setUscitaAnticipata(settings?.checkout_threshold_early || 0);
        setIngressoPosticipato(settings?.checkin_threshold_late || 0);
        setUscitaPosticipata(settings?.checkout_threshold_late || 0);
        setIsGeoLimitActive(settings?.geolocation_active || false);
      } catch (err) {
        setError("Errore durante il caricamento delle impostazioni.");
        console.error(err);
      }
    };

    fetchSettings();
  }, [token]);

  useEffect(() => {
    const fetchLocations = async () => {
      setLoading(true);
      try {
        const response = await getGeolocationLimitList(token);
        setLocations(response || []);        
      } catch (error) {
        console.error("Errore nel recupero delle location", error);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, [token]);

  const handleSave = async () => {
    if (!originalSettings) {
      setError("Impossibile salvare: le impostazioni originali non sono state caricate.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updatedFields = {};

      if (ingressoAnticipato !== originalSettings.checkin_threshold_early) {
        updatedFields.checkin_threshold_early = ingressoAnticipato;
      }
      if (uscitaAnticipata !== originalSettings.checkout_threshold_early) {
        updatedFields.checkout_threshold_early = uscitaAnticipata;
      }
      if (ingressoPosticipato !== originalSettings.checkin_threshold_late) {
        updatedFields.checkin_threshold_late = ingressoPosticipato;
      }
      if (uscitaPosticipata !== originalSettings.checkout_threshold_late) {
        updatedFields.checkout_threshold_late = uscitaPosticipata;
      }

      if (Object.keys(updatedFields).length > 0) {
        await updateCompanySettings(token, updatedFields);
        setOriginalSettings({ ...originalSettings, ...updatedFields });
  
        Swal.fire({
          icon: "success",
          title: "Salvataggio completato",
          text: "Le impostazioni sono state salvate con successo.",
          showConfirmButton: false,
          timer: 1000,
        });
      } else {
        console.log("Nessuna modifica da salvare.");
      }
  
      setIsEditing(false);
    } catch (err) {
      setError("Errore durante il salvataggio delle impostazioni.");
      console.error(err);

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: "Si è verificato un errore durante il salvataggio delle impostazioni. Riprova più tardi.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (originalSettings && isGeoLimitActive !== originalSettings.geolocation_active) {
      const updateGeoSetting = async () => {
        try {
          await updateCompanySettings(token, { geolocation_active: isGeoLimitActive });
          setOriginalSettings((prev) => ({
            ...prev,
            geolocation_active: isGeoLimitActive,
          }));
  
          Swal.fire({
            icon: "success",
            title: "Salvato!",
            showConfirmButton: false,
            timer: 1000,
          });
        } catch (err) {
          setError("Errore durante il salvataggio dell'impostazione di geolocalizzazione.");
          console.error(err);
  
          Swal.fire({
            icon: "error",
            title: "Errore",
            text: "Si è verificato un errore durante il salvataggio della geolocalizzazione. Riprova più tardi.",
          });
        }
      };
  
      updateGeoSetting();
    }
  }, [isGeoLimitActive]);
  
  
  const handleCancel = () => {
    setIsEditing(false);

    if (originalSettings) {
      setIngressoAnticipato(originalSettings.checkin_threshold_early || 0);
      setUscitaAnticipata(originalSettings.checkout_threshold_early || 0);
      setIngressoPosticipato(originalSettings.checkin_threshold_late || 0);
      setUscitaPosticipata(originalSettings.checkout_threshold_late || 0);
    }
  };

  const handleInputChange = (setter) => (e) => {
    const value = e.target.value;
    setter(value === '' || isNaN(value) ? 0 : parseInt(value, 10));
  };

  // const handleEdit = (id) => {
  //   console.log("Modifica sede con ID:", id);

  // };


  const handleAdd = () => setIsLocationPickerOpen(true);
  
  const handleSaveGeolocation = async (payload) => {
    try {
      const response = await createGeolocationLimit(token, payload);
      setLocations([...locations, response]);
      setIsLocationPickerOpen(false);
      
      Swal.fire({
        icon: "success",
        title: "Salvato!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Errore nel salvataggio della geolocalizzazione", error);
      let errorMessage = "Non è stato possibile effettuare il salvataggio. Per favore riprova.";
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorMessages = Object.entries(errorData)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join("\n");

        if (errorMessages) {
          errorMessage = errorMessages;
        }
      }
      
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteGeolocationLimit(token, id);
      setLocations((prevLocations) => prevLocations.filter((location) => location.id !== id));
      Swal.fire({
        icon: "success",
        title: "Eliminato!",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Errore nell'eliminazione della geolocalizzazione", error);
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: "Non è stato possibile eliminare la posizione. Riprova.",
        confirmButtonText: "OK",
      });
    }
  };
  
  const handleCloseMap = () => {
    setIsModalLocationOpen(false);
    setModalLocation([]);
  };

  const handleGeoToggle = () => {
    setIsGeoLimitActive(prev => !prev);
  };
 

  return (
    <div className="max-w-full p-4 mx-auto">

      {/* SEZIONE FLESSIBILITÀ ORARIA */}
      <div className="border border-gray-300 rounded-lg p-3 flex justify-between items-center">
        <span className="text-gray-900 font-medium uppercase">Flessibilità Oraria</span>
        <button
          type="button"
          className="border border-indigo-400 flex items-center rounded-md px-3 py-1 text-sm font-semibold text-white shadow-sm w-24 justify-center bg-indigo-600 hover:bg-indigo-400"
          onClick={() => setShowFlessibilitaOraria(!showFlessibilitaOraria)}
        >
          {showFlessibilitaOraria ? "Nascondi" : "Mostra"}
        </button>
      </div>

      {showFlessibilitaOraria && (
        <div className="border border-gray-300 rounded-lg p-3 mt-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-6">
            Impostare la flessibilità oraria su ingresso e uscita. Se la timbratura
            avviene al di fuori dei limiti impostati verrà ricevuta una segnalazione.
          </h2>

          {/* Limiti di tempo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ingresso Anticipato */}
            <div className="flex items-center">
              <label
                htmlFor="ingressoAnticipato"
                className="text-md font-normal text-gray-900 leading-6"
              >
                Limite di tempo per l'ingresso anticipato
              </label>
            </div>
            <div className="flex items-center px-4">
              {isEditing ? (
                <input
                  id="ingressoAnticipato"
                  type="number"
                  className="w-1/2 py-1 text-md font-normal text-gray-900 leading-6 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  value={ingressoAnticipato}
                  onChange={handleInputChange(setIngressoAnticipato)}
                  min="0"
                  step="1"
                />
              ) : (
                <span className="text-md font-normal text-gray-900 leading-6">{ingressoAnticipato}</span>
              )}
              <span className="ml-2 text-md font-normal text-gray-900 leading-6">minuti</span>
            </div>

            {/* Ingresso Posticipato */}
            <div className="flex items-center">
              <label
                htmlFor="ingressoPosticipato"
                className="text-md font-normal text-gray-900 leading-6"
              >
                Limite di tempo per l'ingresso posticipato
              </label>
            </div>
            <div className="flex items-center px-4">
              {isEditing ? (
                <input
                  id="ingressoPosticipato"
                  type="number"
                  className="w-1/2 py-1 text-md font-normal text-gray-900 leading-6 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  value={ingressoPosticipato}
                  onChange={handleInputChange(setIngressoPosticipato)}
                  min="0"
                  step="1"
                />
              ) : (
                <span className="text-md font-normal text-gray-900 leading-6">{ingressoPosticipato}</span>
              )}
              <span className="ml-2 text-md font-normal text-gray-900 leading-6">minuti</span>
            </div>

            {/* Uscita Anticipata */}
            <div className="flex items-center">
              <label
                htmlFor="uscitaAnticipata"
                className="text-md font-normal text-gray-900 leading-6"
              >
                Limite di tempo per l'uscita anticipata
              </label>
            </div>
            <div className="flex items-center px-4">
              {isEditing ? (
                <input
                  id="uscitaAnticipata"
                  type="number"
                  className="w-1/2 py-1 text-md font-normal text-gray-900 leading-6 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  value={uscitaAnticipata}
                  onChange={handleInputChange(setUscitaAnticipata)}
                  min="0"
                  step="1"
                />
              ) : (
                <span className="text-md font-normal text-gray-900 leading-6">{uscitaAnticipata}</span>
              )}
              <span className="ml-2 text-md font-normal text-gray-900 leading-6">minuti</span>
            </div>

            {/* Uscita Posticipata */}
            <div className="flex items-center">
              <label
                htmlFor="uscitaPosticipata"
                className="text-md font-normal text-gray-900 leading-6"
              >
                Limite di tempo per l'uscita posticipata
              </label>
            </div>
            <div className="flex items-center px-4">
              {isEditing ? (
                <input
                  id="uscitaPosticipata"
                  type="number"
                  className="w-1/2 py-1 text-md font-normal text-gray-900 leading-6 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  value={uscitaPosticipata}
                  onChange={handleInputChange(setUscitaPosticipata)}
                  min="0"
                  step="1"
                />
              ) : (
                <span className="text-md font-normal text-gray-900 leading-6">{uscitaPosticipata}</span>
              )}
              <span className="ml-2 text-md font-normal text-gray-900 leading-6">minuti</span>
            </div>
          </div>

          {/* Pulsanti */}
          <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-7 mt-4">
            <div className="flex justify-center md:justify-end">
              {isEditing ? (
                <button
                  type="button"
                  className="border border-indigo-400 flex items-center rounded-md px-3 py-1 text-center text-sm font-semibold text-white shadow-sm focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 w-24 justify-center mr-3 bg-indigo-600 hover:bg-indigo-400"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Salvataggio..." : "Salva"}
                </button>
              ) : (
                <button
                  type="button"
                  className="border border-indigo-400 flex items-center rounded-md px-3 py-1 text-center text-sm font-semibold text-white shadow-sm focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 w-24 justify-center mr-3 bg-indigo-600 hover:bg-indigo-400"
                  onClick={() => setIsEditing(true)}
                >
                  Modifica
                </button>
              )}
            </div>

            {isEditing && (
              <div className="flex justify-center md:justify-start">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="border border-gray-400 block rounded-md bg-grey-400 px-3 py-1 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-grey-500 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 hover:bg-gray-400 hover:text-white w-24 justify-center"
                >
                  Annulla
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      

      {/* SEZIONE LIMITE TERRITORIO TIMBRATURA */}
      <div className="border border-gray-300 rounded-lg p-3 flex justify-between items-center mt-4">
        <span className="text-gray-900 font-medium uppercase">Limite Geografico</span>
        <button
          type="button"
          className="border border-indigo-400 flex items-center rounded-md px-3 py-1 text-sm font-semibold text-white shadow-sm w-24 justify-center bg-indigo-600 hover:bg-indigo-400"
          onClick={() => setShowLimiteTerritorio(!showLimiteTerritorio)}
        >
          {showLimiteTerritorio ? "Nascondi" : "Mostra"}
        </button>
      </div>

      {showLimiteTerritorio && (
        <div className="border border-gray-300 rounded-lg p-3 mt-4">
          <div className="relative flex items-center py-2 mb-3">
            <div className="min-w-0 flex-1 text-sm font-semibold text-gray-900">
              <div>Attiva il limite geografico sulle timbrature</div>
            </div>
            <div className="ml-3 flex h-6 items-center">
              <button
                type="button"
                onClick={handleGeoToggle}
                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${
                  isGeoLimitActive ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                    isGeoLimitActive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className='ml-2 font-semibold text-gray-800'>Stato: {isGeoLimitActive ? 'Abilitato' : 'Disabilitato'}</span>
            </div>
          </div>
          {!isGeoLimitActive && (
            <span className="font-semibold text-gray-900">
              I dipendenti autorizzati alla timbratura possono registrare la propria presenza da qualsiasi luogo, senza restrizioni geografiche. La posizione della timbratura verrà salvata e sarà visibile sulla mappa.
            </span>
          )}

          {isGeoLimitActive && (
            <>
              <h2 className={`text-sm font-semibold text-gray-900 mb-6 ${isGeoLimitActive ? "" : "opacity-50 pointer-events-none"}`}>
                Salva la posizione della sede ed imposta un raggio entro il quale è consentito effettuare la timbratura. Se il dipendente si trova al di fuori del raggio impostato, la timbratura non potrà essere registrata.
              </h2>

              <div className={`w-full overflow-x-auto max-h-[50vh] overflow-y-auto ${isGeoLimitActive ? "" : "opacity-50 pointer-events-none"}`}>
                <table className="table-auto w-full text-sm border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-4 py-2 text-left border border-gray-300 font-semibold text-gray-600">Nome Sede</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-semibold text-gray-600">Posizione</th>
                      <th className="px-4 py-2 text-left border border-gray-300 font-semibold text-gray-600">Raggio (m)</th>
                      <th className="px-4 py-2 text-center border border-gray-300 font-semibold text-gray-600">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4"><Fetching /></td>
                      </tr>
                    ) : locations.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center py-4"><Empty /></td>
                      </tr>
                    ) : (
                      locations.map((location) => (
                        <tr key={location.id} className="border border-gray-300">
                          <td className="px-4 py-2 border-r border-gray-300">{location.name}</td>
                          <td className="px-4 py-2 border-r border-gray-300 hover:underline cursor-pointer hover:text-indigo-600"
                            onClick={() => {
                              setModalLocation({
                                ...location,
                                latitude: location.lat,
                                longitude: location.lng,
                              });
                              setRadius(location.radius)
                              setIsModalLocationOpen(true);
                            }}
                          >Visualizza posizione</td>
                          <td className="px-4 py-2 border-r border-gray-300">{location.radius} m</td>
                          <td className="px-4 py-2 flex justify-center gap-2">
                            <CustomTooltipComponent title="Elimina" placement="left">
                              <button
                                className="hover:text-red-600"
                                onClick={() => handleDelete(location.id)}
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </CustomTooltipComponent>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
    

              {isModalLocationOpen && modalLocation && (
                <GeolocationModal
                  isOpen={isModalLocationOpen}
                  onClose={() => setIsModalLocationOpen(false)}
                  locations={[modalLocation]}
                  radius={radius}
                />
              )}

              {isLocationPickerOpen && (
                <LocationPicker 
                  onSave={handleSaveGeolocation} 
                  onClose={() => setIsLocationPickerOpen(false)} 
                />
              )}

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  className={`border flex items-center rounded-md px-3 py-1 text-sm font-semibold text-white shadow-sm w-24 justify-center ${
                    isGeoLimitActive
                      ? "bg-indigo-600 hover:bg-indigo-400 border-indigo-400"
                      : "bg-gray-400 border-gray-300 cursor-not-allowed"
                  }`}
                  onClick={handleAdd}
                  disabled={!isGeoLimitActive}
                >
                  Aggiungi
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TimbraturaSetting;
